/**
 * Every tunable in one object (DESIGN.md §3). Nothing in the physics reads a
 * magic number that isn't here — finding the fun means turning these knobs,
 * so they all have to be live-adjustable and serialisable.
 */

export interface TuningConfig {
  // --- body ---
  /** kg */
  mass: number
  /** Standing height used when no calibration has happened yet (m). */
  nominalHeight: number
  /** Body-cylinder radius used for the inertia tensor (m). */
  bodyRadius: number

  // --- assists (trainer → realistic) ---
  /** Multiplies real gravity. 0.4 trainer → 1.0 realistic. */
  gravityScale: number
  /** Multiplies the inertia tensor. 4x trainer → 1x realistic. */
  inertiaScale: number
  /**
   * Angular damping as a **rate**, 1/s: a spin decays with time constant 1/k on
   * every axis. The "flailing limbs / RCS" assist. Expressed this way rather
   * than as N·m/(rad/s) so that yaw — whose inertia is ~20x smaller than
   * pitch/roll — does not get ~20x more damped by the same setting.
   */
  angularDamping: number
  /** 0 = realistic, 1 = strong spring-damper toward upright. */
  autoLevelStrength: number

  // --- rockets ---
  /** Thrust-to-weight of both rockets combined, relative to *effective* weight. */
  twr: number
  /** Throttle spool time constant, seconds. 0 = instant. */
  thrustLag: number
  /** Seconds to decay a rocket's thrust to zero after losing tracking. */
  trackingLossDecay: number

  // --- centre of mass ---
  /** Fraction of standing height at which the CoM sits. */
  comHeightFraction: number
  /** 0 = fixed CoM, ~0.45 = dynamic lean input. */
  comLeanGain: number
  /** Smoothing time constant for the CoM estimate, seconds. */
  comSmoothing: number

  // --- air ---
  airDensity: number
  dragCoefficient: number
  /** Reference area, m². With Cd=1 this sets terminal velocity. */
  dragArea: number

  // --- world ---
  /** Impact speed above which a contact is a crash rather than a landing, m/s. */
  crashSpeed: number
  /** Coulomb friction at ground contacts. */
  groundFriction: number
  /**
   * How hard the pilot's legs hold them upright once standing, 0..1. A point
   * contact has no support polygon, so without this a landed pilot topples —
   * which is correct for a rigid body and wrong for a person.
   */
  standAssist: number
}

export const DEFAULT_CONFIG: TuningConfig = {
  mass: 80,
  nominalHeight: 1.7,
  bodyRadius: 0.15,

  gravityScale: 1,
  inertiaScale: 1,
  angularDamping: 0.6,
  autoLevelStrength: 0,

  twr: 1.5,
  thrustLag: 0,
  trackingLossDecay: 0.2,

  comHeightFraction: 0.57,
  comLeanGain: 0.45,
  comSmoothing: 0.4,

  airDensity: 1.225,
  dragCoefficient: 1.0,
  dragArea: 0.45,

  crashSpeed: 7,
  groundFriction: 0.8,
  standAssist: 1,
}

export type PresetName = 'trainer' | 'sport' | 'realistic'

/**
 * The three difficulty presets from §3. `sport` is the one Phase 1's exit
 * criterion is measured against.
 */
export const PRESETS: Record<PresetName, Partial<TuningConfig>> = {
  trainer: {
    gravityScale: 0.4,
    inertiaScale: 4,
    angularDamping: 1.2,
    autoLevelStrength: 0.55,
    twr: 2.0,
    thrustLag: 0,
    comLeanGain: 0,
    comSmoothing: 0.6,
  },
  sport: {
    gravityScale: 0.7,
    inertiaScale: 2,
    angularDamping: 0.5,
    // I set this to 0 once, on the strength of a coast measurement alone, and
    // it made Sport unflyable — see `npm run sim -- posture`. Holding your
    // hands even 10 cm forward of the CoM is a standing pitch torque that puts
    // you past 45° in 1.6 s with no assist, and nobody holds their hands in
    // the CoM's frontal plane by accident.
    //
    // With the auto-level spring under-damped (ζ 0.22, see Pilot), the spring
    // cancels that standing bias while costing far less spin coast than the
    // critically-damped version did:
    //
    //   autoLevel   15cm posture   30cm posture   roll coast τ
    //   0           runaway        runaway        2.11 s
    //   0.15        30° bounded    45° at 1.2 s   0.23 s
    //   0.25        19° bounded    37° bounded    0.14 s
    //
    // 0.15 forgives a good posture and still punishes a sloppy one.
    autoLevelStrength: 0.15,
    twr: 1.7,
    thrustLag: 0.05,
    comLeanGain: 0.45,
    comSmoothing: 0.4,
  },
  realistic: {
    gravityScale: 1.0,
    inertiaScale: 1,
    angularDamping: 0.08,
    autoLevelStrength: 0,
    twr: 1.3,
    thrustLag: 0.15,
    comLeanGain: 0.45,
    comSmoothing: 0.25,
  },
}

export interface KnobSpec {
  key: keyof TuningConfig
  label: string
  min: number
  max: number
  step: number
  /** Shown in the panel so the direction of "harder" is never ambiguous. */
  hint: string
}

/** The knobs surfaced in the tuning panels, in the order §3 lists them. */
export const KNOBS: KnobSpec[] = [
  { key: 'gravityScale', label: 'gravity', min: 0, max: 1.5, step: 0.05, hint: '0.4 trainer → 1.0 real' },
  { key: 'inertiaScale', label: 'inertia', min: 0.5, max: 6, step: 0.1, hint: '4x trainer → 1x real' },
  { key: 'angularDamping', label: 'ang damping', min: 0, max: 4, step: 0.02, hint: 'spin coasts ≈ 1/k seconds' },
  { key: 'autoLevelStrength', label: 'auto-level', min: 0, max: 1, step: 0.01, hint: 'strong → 0 real' },
  { key: 'twr', label: 'TWR', min: 1.05, max: 3, step: 0.05, hint: 'hover ≈ 1/TWR throttle' },
  { key: 'thrustLag', label: 'spool lag', min: 0, max: 0.3, step: 0.005, hint: '0 → 150 ms' },
  { key: 'comSmoothing', label: 'CoM smoothing', min: 0.02, max: 1.2, step: 0.02, hint: 'heavy → light' },
  { key: 'comLeanGain', label: 'CoM lean gain', min: 0, max: 1, step: 0.01, hint: '0 fixed → 0.45 dynamic' },
  { key: 'mass', label: 'mass (kg)', min: 40, max: 140, step: 1, hint: 'pilot + pack' },
  { key: 'crashSpeed', label: 'crash speed', min: 2, max: 20, step: 0.5, hint: 'm/s impact limit' },
]

export function makeConfig(preset: PresetName = 'sport'): TuningConfig {
  return applyPreset({ ...DEFAULT_CONFIG }, preset)
}

export function applyPreset(cfg: TuningConfig, preset: PresetName): TuningConfig {
  Object.assign(cfg, PRESETS[preset])
  return cfg
}

const STORAGE_KEY = 'rocketmanxr.tuning'
/**
 * Bump whenever a knob's *meaning* changes, not merely its default. A saved
 * config from an older schema is silently wrong rather than merely stale —
 * `angularDamping: 18` meant "gentle" as N·m/(rad/s) and means "instantly
 * frozen" as a 1/s rate — so old saves are dropped instead of merged.
 */
const STORAGE_VERSION = 2

/**
 * Config resolution order: defaults → preset → localStorage → URL params.
 * URL wins so a bad saved config can always be overridden with `?preset=sport`.
 */
export function loadConfig(search = ''): { config: TuningConfig; preset: PresetName } {
  const params = new URLSearchParams(search)
  let preset = (params.get('preset') as PresetName) ?? 'sport'
  if (!(preset in PRESETS)) preset = 'sport'

  const config = makeConfig(preset)

  if (!params.has('preset')) {
    try {
      const saved = globalThis.localStorage?.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved) as {
          version?: number
          preset?: PresetName
          config?: Partial<TuningConfig>
        }
        if (parsed.version !== STORAGE_VERSION) {
          globalThis.localStorage?.removeItem(STORAGE_KEY)
        } else {
          if (parsed.preset && parsed.preset in PRESETS) preset = parsed.preset
          if (parsed.config) mergeNumeric(config, parsed.config)
        }
      }
    } catch {
      /* corrupt storage is not worth crashing over */
    }
  }

  // Any knob can be overridden directly: ?twr=1.9&angularDamping=4
  for (const knob of KNOBS) {
    const raw = params.get(knob.key)
    if (raw !== null && Number.isFinite(Number(raw))) {
      ;(config[knob.key] as number) = Number(raw)
    }
  }

  return { config, preset }
}

export function saveConfig(config: TuningConfig, preset: PresetName): void {
  try {
    globalThis.localStorage?.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: STORAGE_VERSION, preset, config }),
    )
  } catch {
    /* private browsing, quota, etc. */
  }
}

function mergeNumeric(target: TuningConfig, source: Partial<TuningConfig>): void {
  for (const [k, v] of Object.entries(source)) {
    if (typeof v === 'number' && Number.isFinite(v) && k in target) {
      ;(target as unknown as Record<string, number>)[k] = v
    }
  }
}
